import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["open"];
import { OBSIDIAN } from 'HubStyleTokens/colors';
import { useViewport } from 'developer-docs-shared-components/hooks/useViewport';
import { AddIcon, DownCaratIcon, MinusIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import styled from 'styled-components';
import { TOC_CLICK } from '../../tracking/trackerActions';
import { getUsageTracker } from '../../utils/usage-tracker/index';
import { ExpandableHeader, ExpandableOpener } from '../ExpandableComponents';
import { TocLink } from './TocLink';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The +/- icons on a group. Indicates that it's an expandable section.
 */
export const ExpandableCaret = _ref => {
  let {
      open
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(IconHolder, Object.assign({}, rest, {
    icon: open ? MinusIcon : AddIcon,
    size: "100",
    color: "text-core-disabled"
  }));
};

/**
 * At mobile, the header for each expandable section.
 */
const Header = styled(ExpandableHeader).withConfig({
  displayName: "ListItem__Header",
  componentId: "sc-18jp0iz-0"
})(["color:", ";margin:9px 0;padding:4px 9px;"], OBSIDIAN);
const LI = props => {
  const viewport = useViewport();
  return /*#__PURE__*/_jsx(View, Object.assign({
    as: "li",
    display: props.visible ? 'block' : 'none',
    marginBlockEnd: !viewport.isMobile ? '400' : '0',
    padding: "0",
    paddingBlockStart: props.$type === 'item' ? '50' : '0'
  }, props));
};

/**
 * A `li`-wrapped link with a submenu `ul`, if applicable. Renders as
 * expandable sections at mobile and onScroll-rendered submenus at desktop.
 */
const ListItem = ({
  activeToc,
  activeChildToc,
  item: {
    depth,
    id,
    items = [],
    label,
    parent
  }
}) => {
  const viewportContext = useViewport();
  const uiType = !viewportContext || viewportContext && viewportContext.isDesktop ? 'list' : 'accordion';
  const isCategory = items.length > 0;
  const itemType = isCategory ? 'category' : 'item';
  const passthroughProps = {
    activeToc,
    activeChildToc
  };
  const trackEvent = tocLink => {
    const pathname = window.location.pathname.split('/');
    getUsageTracker().track(TOC_CLICK, {
      page: pathname[pathname.length - 1],
      tocLink
    });
  };

  /**
   * The nested unordered list containing items in a category.
   */
  const subMenuEl = /*#__PURE__*/_jsx(View, {
    as: "ul",
    listStyleType: "none",
    marginBlockStart: "400",
    children: items.map(item => /*#__PURE__*/_jsx(ListItem, Object.assign({
      item: item
    }, passthroughProps), item.id))
  });

  /**
   * An item's link element.
   */
  const linkEl = /*#__PURE__*/_jsx(TocLink, {
    active: activeToc === id || activeChildToc === id,
    depth: depth,
    $type: itemType,
    onClick: () => {
      trackEvent(id);
    },
    href: `#${id}`,
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      children: [label, isCategory && /*#__PURE__*/_jsx(View, {
        display: "inline-block",
        marginInlineStart: "300",
        paddingBlockStart: "100",
        children: /*#__PURE__*/_jsx(DownCaratIcon, {
          color: "icon-core-subtle",
          size: "100"
        })
      })]
    })
  });
  return /*#__PURE__*/_jsxs(LI, {
    depth: depth,
    $type: itemType,
    parent: parent,
    visible: uiType === 'list' ? parent === null || parent === activeToc : true,
    children: [uiType === 'list' && /*#__PURE__*/_jsxs(_Fragment, {
      children: [linkEl, isCategory && subMenuEl]
    }), uiType === 'accordion' && /*#__PURE__*/_jsx(_Fragment, {
      children: isCategory ? /*#__PURE__*/_jsx(ExpandableSection, {
        paddingBlockEnd: "200",
        contentClassName: "p-bottom-0",
        indentation: "none",
        titleText: label
        // @ts-expect-error we expect an error on this component. See ExpandableComponents.tsx
        ,
        Header: !viewportContext || viewportContext.isDesktop ? undefined : Header
        // @ts-expect-error we expect an error on this component. See ExpandableComponents.tsx
        ,
        Opener: !viewportContext || viewportContext.isDesktop ? undefined : ExpandableOpener,
        Caret: !viewportContext || viewportContext.isDesktop ? undefined : ExpandableCaret,
        children: subMenuEl
      }) : linkEl
    })]
  }, id);
};
export default ListItem;